// ⚠️  GENERATED — do not edit. Run `npm run inferrex:generate`.
// Runtime Zod validators DERIVED from inferrex-ai/canonical-model/manifest.ts —
// the same source as the TS types, so they cannot drift. Validate inferred
// manifests with e.g. ConnectionManifestSchema.safeParse(value).
import { z } from 'zod';
import type * as M from './model.generated.js';

export const InferredFieldTypeSchema = z.enum(["text", "number", "currency", "percentage", "boolean", "date", "datetime", "email", "phone", "url", "select", "multiselect", "relation", "address", "coordinates", "file", "richtext", "json", "array", "duration", "color", "country_code", "currency_code", "language_code", "unknown"]);
export const FieldAuthorityRuleSchema = z.enum(["provider-wins", "native-only", "native-override", "latest-wins", "merge"]);
export const ConflictStrategySchema = z.enum(["latest-timestamp", "highest-authority", "flag-for-review"]);
export const ConditionOpSchema = z.enum(["equals", "not-equals", "contains", "greater-than", "less-than", "present", "absent"]);
export const RelationTypeSchema = z.enum(["belongsTo", "hasMany", "hasOne", "references"]);
export const EntityCategorySchema = z.enum(["domain", "operation", "transport", "descriptor", "collection", "error", "enum"]);
export const AuthMethodSchema = z.enum(["oauth2", "api-key", "basic", "bearer", "aws-sigv4", "jwt", "mtls", "custom", "none"]);
export const PaginationStyleSchema = z.enum(["offset-limit", "cursor", "page-number", "link-header", "none"]);
export const ConnectionManifestStatusSchema = z.enum(["inferring", "awaiting-review", "approved", "active", "error"]);
export const TransformTypeSchema = z.enum(["direct", "rename", "type-cast", "format", "lookup", "formula", "concatenate", "split", "map-values", "default-value"]);
export const SyncModeSchema = z.enum(["realtime", "near-realtime", "periodic", "archival"]);
export const SyncDirectionSchema = z.enum(["source-to-target", "target-to-source", "bidirectional"]);
export const MappingManifestStatusSchema = z.enum(["draft", "awaiting-review", "approved", "active", "paused"]);
export const PipelineStatusSchema = z.enum(["draft", "reviewing", "active", "paused", "error", "upgrading"]);
export const SuggestionTypeSchema = z.enum(["new-connection", "new-trigger", "new-transform", "archival-policy", "sync-tier-optimisation", "partner-integration"]);
export const LearningEventTypeSchema = z.enum(["field-classification", "relationship-inference", "semantic-interpretation", "cross-system-matching", "consistency-check", "pipeline-suggestion", "healing-resolution", "anomaly-detection", "drift-assessment", "archival-classification", "partner-recommendation", "version-simulation", "customer-approval", "customer-correction", "stack-insight", "entity-classification-v2", "entity-pair-matching-v2", "own-weights-validation"]);
export const ModelProviderSchema = z.enum(["cloudflare", "foundry", "proprietary"]);

export const RefinementResultSchema = z.object({
  value: z.string(),
  confidence: z.number().optional(),
  layer: z.number(),
});
export const InferredFieldSchema = z.object({
  id: z.string(),
  apiName: z.string(),
  displayName: z.string(),
  description: z.string().optional(),
  type: InferredFieldTypeSchema,
  required: z.boolean(),
  nullable: z.boolean(),
  role: z.string().optional(),
  roleConfidence: z.number().optional(),
  roleSource: z.string().optional(),
  sensitivity: z.string().optional(),
  enumValues: z.array(z.string()).optional(),
  currencyCode: z.string().optional(),
  scale: z.number().optional(),
  relatedEntityId: z.string().optional(),
  typeConfidence: z.number(),
  typeRationale: z.string(),
  requiresCustomerReview: z.boolean(),
  authority: FieldAuthorityRuleSchema,
  conflictStrategy: ConflictStrategySchema,
  exampleValues: z.array(z.custom<unknown>()),
  wireType: z.string().optional(),
  wireFormat: z.string().optional(),
  refTarget: z.string().optional(),
  source: z.string().optional(),
  group: z.string().optional(),
  included: z.boolean().optional(),
  nullRate: z.number(),
  uniqueRate: z.number(),
  lastSeen: z.string(),
  valueProfile: z.object({ sampleCount: z.number(), nullRate: z.number(), distinctCount: z.number(), formatHits: z.record(z.string(), z.number()), minLen: z.number(), maxLen: z.number() }).optional(),
  refinements: z.record(z.string(), z.lazy(() => RefinementResultSchema)).optional(),
});
export const InferredEntitySchema = z.object({
  id: z.string(),
  apiName: z.string(),
  displayName: z.string(),
  pluralDisplayName: z.string(),
  description: z.string(),
  fields: z.array(z.lazy(() => InferredFieldSchema)),
  relationships: z.array(z.lazy(() => InferredRelationshipSchema)),
  category: EntityCategorySchema.optional(),
  archetype: z.string().optional(),
  archetypeConfidence: z.number().optional(),
  endpoints: z.lazy(() => EntityEndpointsSchema),
  updateVia: z.enum(['patch', 'put', 'post-to-get']).optional(),
  overallConfidence: z.number(),
  requiresCustomerReview: z.boolean(),
  reviewableFields: z.array(z.string()),
  refinements: z.record(z.string(), z.lazy(() => RefinementResultSchema)).optional(),
});
export const InferredRelationshipSchema = z.object({
  type: RelationTypeSchema,
  targetEntityId: z.string(),
  throughField: z.string(),
  confidence: z.number(),
  rationale: z.string(),
});
export const EntityEndpointsSchema = z.object({
  list: z.string().optional(),
  get: z.string().optional(),
  create: z.string().optional(),
  update: z.string().optional(),
  delete: z.string().optional(),
  search: z.string().optional(),
});
export const AuthConfigSchema = z.object({
  method: AuthMethodSchema,
  scheme: z.string().optional(),
  oauth2: z.object({ authorizeEndpoint: z.string().optional(), tokenEndpoint: z.string(), scopes: z.array(z.string()).optional(), pkce: z.boolean(), refreshable: z.boolean() }).optional(),
  apiKey: z.object({ headerName: z.string(), prefix: z.string().optional() }).optional(),
});
export const PaginationConfigSchema = z.object({
  style: PaginationStyleSchema,
  pageSize: z.number(),
  pageSizeParam: z.string().optional(),
  offsetParam: z.string().optional(),
  pageParam: z.string().optional(),
  cursorParam: z.string().optional(),
  cursorField: z.string().optional(),
  totalField: z.string().optional(),
});
export const RateLimitConfigSchema = z.object({
  requestsPerMinute: z.number(),
  requestsPerDay: z.number().optional(),
  burstLimit: z.number().optional(),
  retryAfterHeader: z.string(),
});
export const ConnectionManifestSchema = z.object({
  id: z.string(),
  version: z.number(),
  customerId: z.string(),
  systemKey: z.string().optional(),
  displayName: z.string(),
  baseUrl: z.string(),
  logoUrl: z.string().optional(),
  entities: z.array(z.lazy(() => InferredEntitySchema)),
  auth: z.lazy(() => AuthConfigSchema),
  pagination: z.lazy(() => PaginationConfigSchema),
  rateLimit: z.lazy(() => RateLimitConfigSchema),
  inferredAt: z.string(),
  inferredBy: z.string(),
  overallConfidence: z.number(),
  requiresReview: z.array(z.string()),
  specSource: z.string().optional(),
  userSpecUrl: z.string().optional(),
  documentationSource: z.string().optional(),
  inferenceMethod: z.enum(['openapi', 'graphql', 'odata', 'mcp', 'documentation', 'probed', 'library', 'cli']),
  status: ConnectionManifestStatusSchema,
  customerApprovedAt: z.string().optional(),
  customerApprovedBy: z.string().optional(),
  previousVersionId: z.string().optional(),
  changeReason: z.string().optional(),
  systemDescription: z.string().optional(),
  industryClassification: z.string().optional(),
  complianceRelevance: z.array(z.string()).optional(),
  corpusPrior: z.object({ providerKey: z.string(), capturedAt: z.string().optional(), ageDays: z.number().optional(), stale: z.boolean(), fieldsTyped: z.number(), fieldsEnriched: z.number(), edgesAdded: z.number(), authApplied: z.boolean(), paginationApplied: z.boolean() }).optional(),
  baseline: z.object({ providerKey: z.string(), sourceConnectionId: z.string(), entitiesMatched: z.number(), entitiesNew: z.number(), fieldsMatched: z.number(), fieldsNew: z.number(), fieldsChanged: z.number() }).optional(),
  verifiedBaseline: z.object({ providerKey: z.string(), sourceConnectionId: z.string(), approvedAt: z.string(), promotedAt: z.string() }).optional(),
  valueSampling: z.object({ entitiesSampled: z.number(), entitiesFailed: z.number(), rowsFetched: z.number(), fieldsProfiled: z.number(), fieldsTyped: z.number(), sampledAt: z.string(), durationMs: z.number(), errors: z.array(z.string()).optional() }).optional(),
  engine: z.string().optional(),
  engineArm: z.string().optional(),
  engineFallbackReason: z.string().optional(),
  modelArm: z.string().optional(),
  writeEncoding: z.enum(['json', 'form']).optional(),
  listVia: z.enum(['endpoint', 'soql']).optional(),
  authError: z.string().optional(),
  pathParams: z.record(z.string(), z.string()).optional(),
  aiLane: z.lazy(() => AiLaneStateSchema).optional(),
});
export const FieldConditionSchema = z.object({
  field: z.string(),
  op: ConditionOpSchema,
  value: z.string().optional(),
});
export const MappingDestinationSchema = z.object({
  id: z.string(),
  kind: z.string(),
  label: z.string(),
  connectionId: z.string().optional(),
  targetEntityId: z.string().optional(),
  targetFieldId: z.string().optional(),
  channelType: z.string().optional(),
  channelTarget: z.string().optional(),
  channelId: z.string().optional(),
  order: z.number(),
  dependsOn: z.string().optional(),
  condition: z.lazy(() => FieldConditionSchema).optional(),
  transform: TransformTypeSchema.optional(),
  transformConfig: z.record(z.string(), z.custom<unknown>()).optional(),
  direction: SyncDirectionSchema.optional(),
  dedupeKeyFields: z.array(z.string()).optional(),
  maxPerMinute: z.number().optional(),
  enabled: z.boolean().optional(),
});
export const FieldMappingSchema = z.object({
  id: z.string(),
  sourceEntityId: z.string(),
  sourceFieldId: z.string(),
  targetEntityId: z.string(),
  targetFieldId: z.string(),
  authority: FieldAuthorityRuleSchema,
  conflictStrategy: ConflictStrategySchema,
  transform: TransformTypeSchema,
  suggestedTransform: z.string().optional(),
  transformConfig: z.record(z.string(), z.custom<unknown>()).optional(),
  syncTier: SyncModeSchema.optional(),
  condition: z.lazy(() => FieldConditionSchema).optional(),
  direction: SyncDirectionSchema.optional(),
  confidence: z.number(),
  matchRationale: z.string(),
  customerApproved: z.boolean(),
  customerRejected: z.boolean().optional(),
  customerModified: z.boolean(),
  autoApplied: z.boolean().optional(),
  omitted: z.boolean().optional(),
  omittedProposalChanged: z.boolean().optional(),
  manualAuthored: z.boolean().optional(),
  manualProposalConflict: z.boolean().optional(),
  matchSource: z.string().optional(),
  destinations: z.array(z.lazy(() => MappingDestinationSchema)).optional(),
});
export const EntityMappingSchema = z.object({
  id: z.string(),
  sourceEntityId: z.string(),
  targetEntityId: z.string(),
  confidence: z.number(),
  entityRelationship: z.string().optional(),
  fieldMappings: z.array(z.lazy(() => FieldMappingSchema)),
  matchOn: z.array(z.string()).optional(),
});
export const FieldGroupDestinationsSchema = z.object({
  group: z.string(),
  destinations: z.array(z.lazy(() => MappingDestinationSchema)),
});
export const SyncConfigSchema = z.object({
  mode: SyncModeSchema,
  direction: SyncDirectionSchema,
  schedule: z.string().optional(),
  intervalMinutes: z.number().optional(),
  realtimeChannel: z.string().optional(),
  sourceType: z.enum(['realtime', 'near-realtime', 'periodic']).optional(),
  streamConfig: z.object({ broker: z.enum(['pubsub', 'nats', 'kafka', 'amqp', 'kinesis']), topic: z.string().optional(), offset: z.enum(['earliest', 'latest']).optional() }).optional(),
  targetType: z.enum(['api', 'warehouse', 'lake']).optional(),
  targetTable: z.string().optional(),
  batchSize: z.number(),
  retryPolicy: z.object({ maxAttempts: z.number(), backoffMs: z.number(), backoffMultiplier: z.number() }),
});
export const MappingManifestSchema = z.object({
  id: z.string(),
  version: z.number(),
  customerId: z.string(),
  sourceConnectionId: z.string(),
  targetConnectionId: z.string(),
  entityMappings: z.array(z.lazy(() => EntityMappingSchema)),
  groupDestinations: z.array(z.lazy(() => FieldGroupDestinationsSchema)).optional(),
  syncConfig: z.lazy(() => SyncConfigSchema),
  matchedAt: z.string(),
  matchedBy: z.string(),
  overallConfidence: z.number(),
  status: MappingManifestStatusSchema,
  customerApprovedAt: z.string().optional(),
  customerApprovedBy: z.string().optional(),
  engine: z.string().optional(),
  engineArm: z.string().optional(),
  engineFallbackReason: z.string().optional(),
  modelArm: z.string().optional(),
});
export const PipelineManifestSchema = z.object({
  id: z.string(),
  version: z.number(),
  customerId: z.string(),
  name: z.string(),
  description: z.string().optional(),
  connectionIds: z.array(z.string()),
  mappingIds: z.array(z.string()),
  status: PipelineStatusSchema,
  alertConfig: z.object({ onSyncFailure: z.boolean(), onDriftDetected: z.boolean(), onAnomalyDetected: z.boolean(), onVersionChange: z.boolean(), webhookUrl: z.string().optional() }),
  archivalEnabled: z.boolean(),
  archivalPolicyIds: z.array(z.string()),
  versionStates: z.record(z.string(), z.string()),
  createdAt: z.string(),
  lastSyncAt: z.string().optional(),
  lastModifiedAt: z.string(),
});
export const PipelineSuggestionSchema = z.object({
  id: z.string(),
  pipelineId: z.string(),
  customerId: z.string(),
  type: SuggestionTypeSchema,
  title: z.string(),
  rationale: z.string(),
  estimatedValue: z.string(),
  confidence: z.number(),
  triggerField: z.string().optional(),
  triggerCondition: z.string().optional(),
  proposedConnection: z.object({ systemName: z.string(), systemKey: z.string().optional(), inferenceRequired: z.boolean(), estimatedSetupTime: z.string() }).optional(),
  status: z.enum(['pending', 'accepted', 'dismissed']),
  surfacedAt: z.string(),
  actionedAt: z.string().optional(),
  triggeredInferenceId: z.string().optional(),
});
export const RawSchemaFieldSchema = z.object({
  name: z.string(),
  declaredType: z.string(),
  description: z.string().optional(),
  required: z.boolean(),
  nullable: z.boolean(),
  exampleValues: z.array(z.custom<unknown>()),
  enumValues: z.array(z.string()).optional(),
});
export const RawSchemaEndpointSchema = z.object({
  path: z.string(),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
  description: z.string().optional(),
  parameters: z.array(z.lazy(() => RawSchemaFieldSchema)),
  requestBody: z.array(z.lazy(() => RawSchemaFieldSchema)).optional(),
  responseFields: z.array(z.lazy(() => RawSchemaFieldSchema)),
  entityName: z.string().optional(),
});
export const RawSecuritySchemeSchema = z.object({
  name: z.string(),
  type: z.string(),
  scheme: z.string().optional(),
  bearerFormat: z.string().optional(),
  in: z.string().optional(),
  paramName: z.string().optional(),
  flows: z.array(z.object({ flow: z.string(), authorizationUrl: z.string().optional(), tokenUrl: z.string().optional(), refreshUrl: z.string().optional(), scopes: z.array(z.string()).optional() })).optional(),
  openIdConnectUrl: z.string().optional(),
  description: z.string().optional(),
});
export const RawSchemaSchema = z.object({
  baseUrl: z.string(),
  title: z.string().optional(),
  version: z.string().optional(),
  endpoints: z.array(z.lazy(() => RawSchemaEndpointSchema)),
  securitySchemes: z.array(z.string()),
  securitySchemeDetails: z.array(z.lazy(() => RawSecuritySchemeSchema)).optional(),
  globalSecurity: z.array(z.string()).optional(),
  discoveredAt: z.string(),
  source: z.enum(['openapi', 'graphql', 'odata', 'documentation', 'probed', 'mcp', 'cli']),
  specUrl: z.string().optional(),
  providerBaseUrl: z.string().optional(),
  entities: z.array(z.lazy(() => RawEntitySchema)).optional(),
  rawSpecJson: z.string().optional(),
});
export const LearningEventSchema = z.object({
  id: z.string(),
  eventType: LearningEventTypeSchema,
  timestamp: z.string(),
  customerId: z.string(),
  pipelineId: z.string().optional(),
  input: z.object({ schema: RawSchemaSchema.partial().optional(), context: z.record(z.string(), z.custom<unknown>()), modelId: z.string(), modelVersion: z.string(), promptVersion: z.string(), layerIndex: z.number().optional() }),
  output: z.object({ result: z.record(z.string(), z.custom<unknown>()), confidence: z.number(), latencyMs: z.number(), tokenCount: z.object({ input: z.number(), output: z.number() }), costUsd: z.number() }),
  outcome: z.object({ customerAccepted: z.boolean(), customerCorrection: z.record(z.string(), z.custom<unknown>()).optional(), healingRequired: z.boolean(), healingResolution: z.string().optional(), downstreamSuccess: z.boolean() }),
  signal: z.object({ isPositive: z.boolean(), correctionDelta: z.number(), qualityScore: z.number(), includeInTraining: z.boolean() }),
});
export const ConsistencyIssueSchema = z.object({
  type: z.enum(['type-mismatch', 'missing-entity', 'circular-relationship', 'low-confidence-field', 'authority-conflict', 'transform-incompatible']),
  layerIndex: z.number(),
  affectedFieldId: z.string().optional(),
  affectedEntityId: z.string().optional(),
  description: z.string(),
  severity: z.enum(['critical', 'warning', 'info']),
  autoRemediable: z.boolean(),
  remediationStrategy: z.string().optional(),
});
export const ConsistencyReportSchema = z.object({
  passed: z.boolean(),
  issues: z.array(z.lazy(() => ConsistencyIssueSchema)),
  requiresReview: z.array(z.string()),
  overallConfidence: z.number(),
  checkedAt: z.string(),
});
export const InferencePipelineRunSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  inputUrl: z.string(),
  status: z.enum(['running', 'awaiting-review', 'completed', 'failed']),
  layer1Output: z.lazy(() => RawSchemaSchema).optional(),
  layer2Output: z.record(z.string(), z.custom<unknown>()).optional(),
  layer3Output: z.record(z.string(), z.custom<unknown>()).optional(),
  layer4Output: z.record(z.string(), z.custom<unknown>()).optional(),
  layer5Output: z.record(z.string(), z.custom<unknown>()).optional(),
  layer6Report: z.lazy(() => ConsistencyReportSchema).optional(),
  finalManifestId: z.string().optional(),
  learningEventIds: z.array(z.string()),
  startedAt: z.string(),
  completedAt: z.string().optional(),
  layerLatencies: z.record(z.string(), z.number()),
  totalCostUsd: z.number(),
  retryCount: z.number(),
  failureReason: z.string().optional(),
});
export const RawFieldSchema = z.object({
  name: z.string(),
  declaredType: z.string(),
  isRequired: z.boolean(),
  description: z.string().optional(),
  example: z.custom<unknown>().optional(),
  enum: z.array(z.custom<unknown>()).optional(),
  format: z.string().optional(),
  refTarget: z.string().optional(),
  nullable: z.boolean().optional(),
});
export const RawEntitySchema = z.object({
  key: z.string(),
  displayName: z.string(),
  fields: z.array(z.lazy(() => RawFieldSchema)),
  sourceEndpoints: z.array(z.string()),
});
export const ClassifiedFieldSchema = InferredFieldSchema.extend({
  rawFieldName: z.string(),
  rawDeclaredType: z.string(),
});
export const EntityGraphSchema = z.object({
  entities: z.array(z.lazy(() => InferredEntitySchema)),
  relationships: z.array(z.object({ sourceEntityId: z.string(), targetEntityId: z.string(), throughField: z.string(), relationType: z.string(), confidence: z.number(), rationale: z.string() })),
});
export const InterpretedSchemaSchema = z.object({
  entities: z.array(z.lazy(() => InferredEntitySchema)),
  systemDescription: z.string(),
  industryClassification: z.string(),
  complianceRelevance: z.array(z.string()),
});
export const FieldMatchProposalSchema = z.object({
  sourceEntityId: z.string(),
  sourceFieldId: z.string(),
  targetEntityId: z.string(),
  targetFieldId: z.string(),
  confidence: z.number(),
  matchRationale: z.string(),
  suggestedTransform: z.string().optional(),
  suggestedAuthority: z.string().optional(),
  entityRelationship: z.string().optional(),
  matchSource: z.string().optional(),
});
export const SyncBatchSchema = z.object({
  pipelineId: z.string(),
  records: z.array(z.record(z.string(), z.custom<unknown>())),
  sourceManifest: z.lazy(() => ConnectionManifestSchema),
  targetManifest: z.lazy(() => ConnectionManifestSchema),
  mappings: z.array(z.lazy(() => FieldMappingSchema)),
});
export const AnomalySchema = z.object({
  recordId: z.string(),
  fieldId: z.string(),
  expectedPattern: z.string(),
  actualValue: z.custom<unknown>(),
  severity: z.enum(['critical', 'high', 'medium', 'low']),
  description: z.string(),
  autoRemediable: z.boolean(),
  remediationStrategy: z.string().optional(),
});
export const SuggestionContextSchema = z.object({
  customerId: z.string(),
  pipelineId: z.string(),
  sourceManifest: z.lazy(() => ConnectionManifestSchema),
  targetManifest: z.lazy(() => ConnectionManifestSchema),
  approvedMappings: z.array(z.lazy(() => FieldMappingSchema)),
  existingConnections: z.array(z.string()),
  usagePatterns: z.record(z.string(), z.custom<unknown>()),
});
export const ProviderConfigSchema = z.object({
  provider: ModelProviderSchema,
  modelId: z.string(),
  maxTokens: z.number(),
  temperature: z.number(),
  structuredOutput: z.boolean().optional(),
  reasoningEffort: z.enum(['none', 'minimal', 'low', 'medium', 'high']).optional(),
  thinkingBudget: z.number().optional(),
});
export const ModelCallResultSchema = z.object({
  text: z.string(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  costUsd: z.number(),
  provider: z.string(),
  modelId: z.string(),
  latencyMs: z.number(),
  computeTier: z.enum(['cpu', 'gpu']).optional(),
  chainPosition: z.number().optional(),
  fallbackFrom: z.string().optional(),
});
// ModelRouter: behavioural type (method member) — no runtime data schema.
export const EntityResidualFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  wireType: z.string().optional(),
  wireFormat: z.string().optional(),
  description: z.string().optional(),
  enumValues: z.array(z.string()).optional(),
  exampleValues: z.array(z.custom<unknown>()),
  refTarget: z.string().optional(),
  required: z.boolean(),
  spineType: z.string().optional(),
  spineRole: z.string().optional(),
  spineConfidence: z.number().optional(),
  valueProfile: z.object({ sampleCount: z.number(), nullRate: z.number(), distinctCount: z.number(), formatHits: z.record(z.string(), z.number()), minLen: z.number(), maxLen: z.number() }).optional(),
});
export const EntityResidualInputSchema = z.object({
  entity: z.object({ id: z.string(), apiName: z.string(), displayName: z.string(), description: z.string().optional(), spineArchetype: z.string().optional(), spineCategory: z.string().optional(), relationships: z.array(z.object({ throughField: z.string(), targetEntity: z.string(), type: z.string() })) }),
  fields: z.array(z.lazy(() => EntityResidualFieldSchema)),
  system: z.object({ title: z.string().optional(), vendorKey: z.string().optional(), source: z.string(), industryHint: z.string().optional(), entityNames: z.array(z.string()) }),
});
export const EntityFieldClassificationSchema = z.object({
  id: z.string(),
  type: z.string(),
  role: z.string(),
  piiClass: z.string(),
  numericSubType: z.string(),
  description: z.string(),
  confidence: z.number(),
  rationale: z.string(),
});
export const EntityClassificationResultSchema = z.object({
  entityId: z.string(),
  archetype: z.string(),
  archetypeConfidence: z.number(),
  category: z.string(),
  description: z.string(),
  fields: z.array(z.lazy(() => EntityFieldClassificationSchema)),
});
export const AiPipelineV2ResponseSchema = z.object({
  modelArm: z.string().optional(),
  engineUnavailable: z.boolean().optional(),
  unavailableReason: z.string().optional(),
  unavailableDetail: z.string().optional(),
  entities: z.array(z.lazy(() => InferredEntitySchema)),
  auth: z.lazy(() => AuthConfigSchema),
  pagination: z.lazy(() => PaginationConfigSchema),
  rateLimit: z.lazy(() => RateLimitConfigSchema),
  overallConfidence: z.number(),
  requiresReview: z.array(z.string()),
  layerLatencies: z.record(z.string(), z.number()),
  totalCostUsd: z.number(),
  learningEventIds: z.array(z.string()),
  systemDescription: z.string().optional(),
  industryClassification: z.string().optional(),
  complianceRelevance: z.array(z.string()).optional(),
  consistency: z.object({ passed: z.boolean(), issueCount: z.number(), issues: z.array(z.lazy(() => ConsistencyIssueSchema)) }).optional(),
  engine: z.string(),
  entitiesClassified: z.number(),
  entitiesEscalated: z.number(),
  hostedTokens: z.number(),
  providersUsed: z.array(z.string()),
  classifiedEntityIds: z.array(z.string()).optional(),
  unservedReason: z.string().optional(),
});
export const AiLaneStateSchema = z.object({
  lane: z.string(),
  state: z.string(),
  scheduledFor: z.string().optional(),
  batchId: z.string().optional(),
  submittedAt: z.string().optional(),
  appliedAt: z.string().optional(),
  entitiesSubmitted: z.number().optional(),
  entitiesApplied: z.number().optional(),
  reservedTokens: z.number().optional(),
  detail: z.string().optional(),
});
export const AiPipelineBatchResponseSchema = z.object({
  engineUnavailable: z.boolean().optional(),
  unavailableReason: z.string().optional(),
  unavailableDetail: z.string().optional(),
  batchId: z.string().optional(),
  status: z.string().optional(),
  terminal: z.boolean().optional(),
  deployment: z.string().optional(),
  submittedEntityIds: z.array(z.string()).optional(),
  reservedTokens: z.number().optional(),
  requestsTotal: z.number().optional(),
  requestsCompleted: z.number().optional(),
  requestsFailed: z.number().optional(),
  filesDeleted: z.number().optional(),
});
export const EntityPairFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  role: z.string().optional(),
  wireType: z.string().optional(),
  wireFormat: z.string().optional(),
  description: z.string().optional(),
  enumValues: z.array(z.string()).optional(),
  exampleValues: z.array(z.custom<unknown>()),
  required: z.boolean(),
  piiClass: z.string().optional(),
});
export const EntityPairSideSchema = z.object({
  id: z.string(),
  apiName: z.string(),
  displayName: z.string(),
  description: z.string().optional(),
  archetype: z.string().optional(),
  vendorKey: z.string().optional(),
  fields: z.array(z.lazy(() => EntityPairFieldSchema)),
});
export const EntityPairPriorSchema = z.object({
  sourceFieldId: z.string(),
  targetFieldId: z.string(),
  confidence: z.number(),
  matchSource: z.string(),
});
export const EntityPairCandidateSchema = z.object({
  sourceFieldId: z.string(),
  targetFieldIds: z.array(z.string()),
});
export const EntityPairInputSchema = z.object({
  source: z.lazy(() => EntityPairSideSchema),
  target: z.lazy(() => EntityPairSideSchema),
  priorPairs: z.array(z.lazy(() => EntityPairPriorSchema)),
  candidates: z.array(z.lazy(() => EntityPairCandidateSchema)),
  unmatchedSourceFieldIds: z.array(z.string()),
  operatorNotes: z.string().optional(),
});
export const EntityPairMatchSchema = z.object({
  sourceFieldId: z.string(),
  targetFieldId: z.string(),
  confidence: z.number(),
  transform: z.string(),
  transformLabel: z.string(),
  authority: z.string(),
  rationale: z.string(),
  additionalSourceFieldIds: z.array(z.string()).optional(),
});
export const EntityPairMatchResultSchema = z.object({
  sourceEntityId: z.string(),
  targetEntityId: z.string(),
  entityRelationship: z.string(),
  entityConfidence: z.number(),
  pairs: z.array(z.lazy(() => EntityPairMatchSchema)),
});
export const AiMatchV2ResponseSchema = z.object({
  modelArm: z.string().optional(),
  engineUnavailable: z.boolean().optional(),
  unavailableReason: z.string().optional(),
  unavailableDetail: z.string().optional(),
  mapping: z.lazy(() => MappingManifestSchema),
  learningEventIds: z.array(z.string()),
  engine: z.string(),
  stages: z.record(z.string(), z.number()),
  layerLatencies: z.record(z.string(), z.number()),
  totalCostUsd: z.number(),
  hostedTokens: z.number(),
  providersUsed: z.array(z.string()),
});
export const AiPipelineResponseSchema = z.object({
  modelArm: z.string().optional(),
  entities: z.array(z.lazy(() => InferredEntitySchema)),
  auth: z.lazy(() => AuthConfigSchema),
  pagination: z.lazy(() => PaginationConfigSchema),
  rateLimit: z.lazy(() => RateLimitConfigSchema),
  overallConfidence: z.number(),
  requiresReview: z.array(z.string()),
  layerLatencies: z.record(z.string(), z.number()),
  totalCostUsd: z.number(),
  learningEventIds: z.array(z.string()),
  systemDescription: z.string().optional(),
  industryClassification: z.string().optional(),
  complianceRelevance: z.array(z.string()).optional(),
  consistency: z.object({ passed: z.boolean(), issueCount: z.number(), issues: z.array(z.lazy(() => ConsistencyIssueSchema)) }).optional(),
});
export const AiMatchResponseSchema = z.object({
  mapping: z.lazy(() => MappingManifestSchema),
  learningEventIds: z.array(z.string()),
});
export const ChangeClassificationSchema = z.object({
  changeType: z.enum(['breaking', 'non-breaking', 'deprecation', 'enhancement', 'cosmetic']),
  severity: z.enum(['critical', 'medium', 'low']),
  affectedEntities: z.array(z.string()),
  summary: z.string(),
  autoResolvable: z.boolean(),
});
export const IntelligenceClassificationSchema = z.object({
  entityClassifications: z.array(z.object({ entityId: z.string(), category: z.string(), value: z.number(), rationale: z.string() })),
  wasteDetections: z.array(z.object({ entityId: z.string(), wasteType: z.string(), estimatedSaving: z.number() })),
});
export const SuggestionProposalSchema = z.object({
  type: z.string(),
  title: z.string(),
  rationale: z.string(),
  estimatedValue: z.string(),
  confidence: z.number(),
  proposedConnection: z.object({ systemName: z.string(), systemKey: z.string().optional() }).optional(),
});
export const VersionSimulationSchema = z.object({
  affectedFieldCount: z.number(),
  breakingFieldCount: z.number(),
  estimated: z.boolean(),
  recommendations: z.array(z.object({ fieldId: z.string(), action: z.string(), rationale: z.string() })),
  autoUpgradable: z.boolean(),
  estimatedDowntime: z.string(),
  riskLevel: z.enum(['high', 'medium', 'low']),
});
export const MigrationOpSchema = z.object({
  op: z.enum(['retarget-source-field', 'retarget-source-entity', 'drop-mapping', 'add-mapping', 'set-transform', 'no-op']),
  mappingId: z.string().optional(),
  toSourceEntityId: z.string().optional(),
  toSourceFieldId: z.string().optional(),
  toTransform: TransformTypeSchema.optional(),
  add: z.object({ sourceEntityId: z.string(), sourceFieldId: z.string(), targetEntityId: z.string(), targetFieldId: z.string() }).optional(),
  rationale: z.string(),
});
export const MigrationPlanSchema = z.object({
  ops: z.array(z.lazy(() => MigrationOpSchema)),
  unresolved: z.array(z.string()),
  summary: z.string(),
});

/* eslint-disable @typescript-eslint/no-unused-vars */
const _drift_RefinementResult: (M.RefinementResult extends z.infer<typeof RefinementResultSchema> ? true : never) & (keyof z.infer<typeof RefinementResultSchema> extends keyof M.RefinementResult ? true : never) & (keyof M.RefinementResult extends keyof z.infer<typeof RefinementResultSchema> ? true : never) = true;
void _drift_RefinementResult;
const _drift_InferredField: (M.InferredField extends z.infer<typeof InferredFieldSchema> ? true : never) & (keyof z.infer<typeof InferredFieldSchema> extends keyof M.InferredField ? true : never) & (keyof M.InferredField extends keyof z.infer<typeof InferredFieldSchema> ? true : never) = true;
void _drift_InferredField;
const _drift_InferredEntity: (M.InferredEntity extends z.infer<typeof InferredEntitySchema> ? true : never) & (keyof z.infer<typeof InferredEntitySchema> extends keyof M.InferredEntity ? true : never) & (keyof M.InferredEntity extends keyof z.infer<typeof InferredEntitySchema> ? true : never) = true;
void _drift_InferredEntity;
const _drift_InferredRelationship: (M.InferredRelationship extends z.infer<typeof InferredRelationshipSchema> ? true : never) & (keyof z.infer<typeof InferredRelationshipSchema> extends keyof M.InferredRelationship ? true : never) & (keyof M.InferredRelationship extends keyof z.infer<typeof InferredRelationshipSchema> ? true : never) = true;
void _drift_InferredRelationship;
const _drift_EntityEndpoints: (M.EntityEndpoints extends z.infer<typeof EntityEndpointsSchema> ? true : never) & (keyof z.infer<typeof EntityEndpointsSchema> extends keyof M.EntityEndpoints ? true : never) & (keyof M.EntityEndpoints extends keyof z.infer<typeof EntityEndpointsSchema> ? true : never) = true;
void _drift_EntityEndpoints;
const _drift_AuthConfig: (M.AuthConfig extends z.infer<typeof AuthConfigSchema> ? true : never) & (keyof z.infer<typeof AuthConfigSchema> extends keyof M.AuthConfig ? true : never) & (keyof M.AuthConfig extends keyof z.infer<typeof AuthConfigSchema> ? true : never) = true;
void _drift_AuthConfig;
const _drift_PaginationConfig: (M.PaginationConfig extends z.infer<typeof PaginationConfigSchema> ? true : never) & (keyof z.infer<typeof PaginationConfigSchema> extends keyof M.PaginationConfig ? true : never) & (keyof M.PaginationConfig extends keyof z.infer<typeof PaginationConfigSchema> ? true : never) = true;
void _drift_PaginationConfig;
const _drift_RateLimitConfig: (M.RateLimitConfig extends z.infer<typeof RateLimitConfigSchema> ? true : never) & (keyof z.infer<typeof RateLimitConfigSchema> extends keyof M.RateLimitConfig ? true : never) & (keyof M.RateLimitConfig extends keyof z.infer<typeof RateLimitConfigSchema> ? true : never) = true;
void _drift_RateLimitConfig;
const _drift_ConnectionManifest: (M.ConnectionManifest extends z.infer<typeof ConnectionManifestSchema> ? true : never) & (keyof z.infer<typeof ConnectionManifestSchema> extends keyof M.ConnectionManifest ? true : never) & (keyof M.ConnectionManifest extends keyof z.infer<typeof ConnectionManifestSchema> ? true : never) = true;
void _drift_ConnectionManifest;
const _drift_FieldCondition: (M.FieldCondition extends z.infer<typeof FieldConditionSchema> ? true : never) & (keyof z.infer<typeof FieldConditionSchema> extends keyof M.FieldCondition ? true : never) & (keyof M.FieldCondition extends keyof z.infer<typeof FieldConditionSchema> ? true : never) = true;
void _drift_FieldCondition;
const _drift_MappingDestination: (M.MappingDestination extends z.infer<typeof MappingDestinationSchema> ? true : never) & (keyof z.infer<typeof MappingDestinationSchema> extends keyof M.MappingDestination ? true : never) & (keyof M.MappingDestination extends keyof z.infer<typeof MappingDestinationSchema> ? true : never) = true;
void _drift_MappingDestination;
const _drift_FieldMapping: (M.FieldMapping extends z.infer<typeof FieldMappingSchema> ? true : never) & (keyof z.infer<typeof FieldMappingSchema> extends keyof M.FieldMapping ? true : never) & (keyof M.FieldMapping extends keyof z.infer<typeof FieldMappingSchema> ? true : never) = true;
void _drift_FieldMapping;
const _drift_EntityMapping: (M.EntityMapping extends z.infer<typeof EntityMappingSchema> ? true : never) & (keyof z.infer<typeof EntityMappingSchema> extends keyof M.EntityMapping ? true : never) & (keyof M.EntityMapping extends keyof z.infer<typeof EntityMappingSchema> ? true : never) = true;
void _drift_EntityMapping;
const _drift_FieldGroupDestinations: (M.FieldGroupDestinations extends z.infer<typeof FieldGroupDestinationsSchema> ? true : never) & (keyof z.infer<typeof FieldGroupDestinationsSchema> extends keyof M.FieldGroupDestinations ? true : never) & (keyof M.FieldGroupDestinations extends keyof z.infer<typeof FieldGroupDestinationsSchema> ? true : never) = true;
void _drift_FieldGroupDestinations;
const _drift_SyncConfig: (M.SyncConfig extends z.infer<typeof SyncConfigSchema> ? true : never) & (keyof z.infer<typeof SyncConfigSchema> extends keyof M.SyncConfig ? true : never) & (keyof M.SyncConfig extends keyof z.infer<typeof SyncConfigSchema> ? true : never) = true;
void _drift_SyncConfig;
const _drift_MappingManifest: (M.MappingManifest extends z.infer<typeof MappingManifestSchema> ? true : never) & (keyof z.infer<typeof MappingManifestSchema> extends keyof M.MappingManifest ? true : never) & (keyof M.MappingManifest extends keyof z.infer<typeof MappingManifestSchema> ? true : never) = true;
void _drift_MappingManifest;
const _drift_PipelineManifest: (M.PipelineManifest extends z.infer<typeof PipelineManifestSchema> ? true : never) & (keyof z.infer<typeof PipelineManifestSchema> extends keyof M.PipelineManifest ? true : never) & (keyof M.PipelineManifest extends keyof z.infer<typeof PipelineManifestSchema> ? true : never) = true;
void _drift_PipelineManifest;
const _drift_PipelineSuggestion: (M.PipelineSuggestion extends z.infer<typeof PipelineSuggestionSchema> ? true : never) & (keyof z.infer<typeof PipelineSuggestionSchema> extends keyof M.PipelineSuggestion ? true : never) & (keyof M.PipelineSuggestion extends keyof z.infer<typeof PipelineSuggestionSchema> ? true : never) = true;
void _drift_PipelineSuggestion;
const _drift_RawSchemaField: (M.RawSchemaField extends z.infer<typeof RawSchemaFieldSchema> ? true : never) & (keyof z.infer<typeof RawSchemaFieldSchema> extends keyof M.RawSchemaField ? true : never) & (keyof M.RawSchemaField extends keyof z.infer<typeof RawSchemaFieldSchema> ? true : never) = true;
void _drift_RawSchemaField;
const _drift_RawSchemaEndpoint: (M.RawSchemaEndpoint extends z.infer<typeof RawSchemaEndpointSchema> ? true : never) & (keyof z.infer<typeof RawSchemaEndpointSchema> extends keyof M.RawSchemaEndpoint ? true : never) & (keyof M.RawSchemaEndpoint extends keyof z.infer<typeof RawSchemaEndpointSchema> ? true : never) = true;
void _drift_RawSchemaEndpoint;
const _drift_RawSecurityScheme: (M.RawSecurityScheme extends z.infer<typeof RawSecuritySchemeSchema> ? true : never) & (keyof z.infer<typeof RawSecuritySchemeSchema> extends keyof M.RawSecurityScheme ? true : never) & (keyof M.RawSecurityScheme extends keyof z.infer<typeof RawSecuritySchemeSchema> ? true : never) = true;
void _drift_RawSecurityScheme;
const _drift_RawSchema: (M.RawSchema extends z.infer<typeof RawSchemaSchema> ? true : never) & (keyof z.infer<typeof RawSchemaSchema> extends keyof M.RawSchema ? true : never) & (keyof M.RawSchema extends keyof z.infer<typeof RawSchemaSchema> ? true : never) = true;
void _drift_RawSchema;
const _drift_LearningEvent: (M.LearningEvent extends z.infer<typeof LearningEventSchema> ? true : never) & (keyof z.infer<typeof LearningEventSchema> extends keyof M.LearningEvent ? true : never) & (keyof M.LearningEvent extends keyof z.infer<typeof LearningEventSchema> ? true : never) = true;
void _drift_LearningEvent;
const _drift_ConsistencyIssue: (M.ConsistencyIssue extends z.infer<typeof ConsistencyIssueSchema> ? true : never) & (keyof z.infer<typeof ConsistencyIssueSchema> extends keyof M.ConsistencyIssue ? true : never) & (keyof M.ConsistencyIssue extends keyof z.infer<typeof ConsistencyIssueSchema> ? true : never) = true;
void _drift_ConsistencyIssue;
const _drift_ConsistencyReport: (M.ConsistencyReport extends z.infer<typeof ConsistencyReportSchema> ? true : never) & (keyof z.infer<typeof ConsistencyReportSchema> extends keyof M.ConsistencyReport ? true : never) & (keyof M.ConsistencyReport extends keyof z.infer<typeof ConsistencyReportSchema> ? true : never) = true;
void _drift_ConsistencyReport;
const _drift_InferencePipelineRun: (M.InferencePipelineRun extends z.infer<typeof InferencePipelineRunSchema> ? true : never) & (keyof z.infer<typeof InferencePipelineRunSchema> extends keyof M.InferencePipelineRun ? true : never) & (keyof M.InferencePipelineRun extends keyof z.infer<typeof InferencePipelineRunSchema> ? true : never) = true;
void _drift_InferencePipelineRun;
const _drift_RawField: (M.RawField extends z.infer<typeof RawFieldSchema> ? true : never) & (keyof z.infer<typeof RawFieldSchema> extends keyof M.RawField ? true : never) & (keyof M.RawField extends keyof z.infer<typeof RawFieldSchema> ? true : never) = true;
void _drift_RawField;
const _drift_RawEntity: (M.RawEntity extends z.infer<typeof RawEntitySchema> ? true : never) & (keyof z.infer<typeof RawEntitySchema> extends keyof M.RawEntity ? true : never) & (keyof M.RawEntity extends keyof z.infer<typeof RawEntitySchema> ? true : never) = true;
void _drift_RawEntity;
const _drift_ClassifiedField: (M.ClassifiedField extends z.infer<typeof ClassifiedFieldSchema> ? true : never) & (keyof z.infer<typeof ClassifiedFieldSchema> extends keyof M.ClassifiedField ? true : never) & (keyof M.ClassifiedField extends keyof z.infer<typeof ClassifiedFieldSchema> ? true : never) = true;
void _drift_ClassifiedField;
const _drift_EntityGraph: (M.EntityGraph extends z.infer<typeof EntityGraphSchema> ? true : never) & (keyof z.infer<typeof EntityGraphSchema> extends keyof M.EntityGraph ? true : never) & (keyof M.EntityGraph extends keyof z.infer<typeof EntityGraphSchema> ? true : never) = true;
void _drift_EntityGraph;
const _drift_InterpretedSchema: (M.InterpretedSchema extends z.infer<typeof InterpretedSchemaSchema> ? true : never) & (keyof z.infer<typeof InterpretedSchemaSchema> extends keyof M.InterpretedSchema ? true : never) & (keyof M.InterpretedSchema extends keyof z.infer<typeof InterpretedSchemaSchema> ? true : never) = true;
void _drift_InterpretedSchema;
const _drift_FieldMatchProposal: (M.FieldMatchProposal extends z.infer<typeof FieldMatchProposalSchema> ? true : never) & (keyof z.infer<typeof FieldMatchProposalSchema> extends keyof M.FieldMatchProposal ? true : never) & (keyof M.FieldMatchProposal extends keyof z.infer<typeof FieldMatchProposalSchema> ? true : never) = true;
void _drift_FieldMatchProposal;
const _drift_SyncBatch: (M.SyncBatch extends z.infer<typeof SyncBatchSchema> ? true : never) & (keyof z.infer<typeof SyncBatchSchema> extends keyof M.SyncBatch ? true : never) & (keyof M.SyncBatch extends keyof z.infer<typeof SyncBatchSchema> ? true : never) = true;
void _drift_SyncBatch;
const _drift_Anomaly: (M.Anomaly extends z.infer<typeof AnomalySchema> ? true : never) & (keyof z.infer<typeof AnomalySchema> extends keyof M.Anomaly ? true : never) & (keyof M.Anomaly extends keyof z.infer<typeof AnomalySchema> ? true : never) = true;
void _drift_Anomaly;
const _drift_SuggestionContext: (M.SuggestionContext extends z.infer<typeof SuggestionContextSchema> ? true : never) & (keyof z.infer<typeof SuggestionContextSchema> extends keyof M.SuggestionContext ? true : never) & (keyof M.SuggestionContext extends keyof z.infer<typeof SuggestionContextSchema> ? true : never) = true;
void _drift_SuggestionContext;
const _drift_ProviderConfig: (M.ProviderConfig extends z.infer<typeof ProviderConfigSchema> ? true : never) & (keyof z.infer<typeof ProviderConfigSchema> extends keyof M.ProviderConfig ? true : never) & (keyof M.ProviderConfig extends keyof z.infer<typeof ProviderConfigSchema> ? true : never) = true;
void _drift_ProviderConfig;
const _drift_ModelCallResult: (M.ModelCallResult extends z.infer<typeof ModelCallResultSchema> ? true : never) & (keyof z.infer<typeof ModelCallResultSchema> extends keyof M.ModelCallResult ? true : never) & (keyof M.ModelCallResult extends keyof z.infer<typeof ModelCallResultSchema> ? true : never) = true;
void _drift_ModelCallResult;
const _drift_EntityResidualField: (M.EntityResidualField extends z.infer<typeof EntityResidualFieldSchema> ? true : never) & (keyof z.infer<typeof EntityResidualFieldSchema> extends keyof M.EntityResidualField ? true : never) & (keyof M.EntityResidualField extends keyof z.infer<typeof EntityResidualFieldSchema> ? true : never) = true;
void _drift_EntityResidualField;
const _drift_EntityResidualInput: (M.EntityResidualInput extends z.infer<typeof EntityResidualInputSchema> ? true : never) & (keyof z.infer<typeof EntityResidualInputSchema> extends keyof M.EntityResidualInput ? true : never) & (keyof M.EntityResidualInput extends keyof z.infer<typeof EntityResidualInputSchema> ? true : never) = true;
void _drift_EntityResidualInput;
const _drift_EntityFieldClassification: (M.EntityFieldClassification extends z.infer<typeof EntityFieldClassificationSchema> ? true : never) & (keyof z.infer<typeof EntityFieldClassificationSchema> extends keyof M.EntityFieldClassification ? true : never) & (keyof M.EntityFieldClassification extends keyof z.infer<typeof EntityFieldClassificationSchema> ? true : never) = true;
void _drift_EntityFieldClassification;
const _drift_EntityClassificationResult: (M.EntityClassificationResult extends z.infer<typeof EntityClassificationResultSchema> ? true : never) & (keyof z.infer<typeof EntityClassificationResultSchema> extends keyof M.EntityClassificationResult ? true : never) & (keyof M.EntityClassificationResult extends keyof z.infer<typeof EntityClassificationResultSchema> ? true : never) = true;
void _drift_EntityClassificationResult;
const _drift_AiPipelineV2Response: (M.AiPipelineV2Response extends z.infer<typeof AiPipelineV2ResponseSchema> ? true : never) & (keyof z.infer<typeof AiPipelineV2ResponseSchema> extends keyof M.AiPipelineV2Response ? true : never) & (keyof M.AiPipelineV2Response extends keyof z.infer<typeof AiPipelineV2ResponseSchema> ? true : never) = true;
void _drift_AiPipelineV2Response;
const _drift_AiLaneState: (M.AiLaneState extends z.infer<typeof AiLaneStateSchema> ? true : never) & (keyof z.infer<typeof AiLaneStateSchema> extends keyof M.AiLaneState ? true : never) & (keyof M.AiLaneState extends keyof z.infer<typeof AiLaneStateSchema> ? true : never) = true;
void _drift_AiLaneState;
const _drift_AiPipelineBatchResponse: (M.AiPipelineBatchResponse extends z.infer<typeof AiPipelineBatchResponseSchema> ? true : never) & (keyof z.infer<typeof AiPipelineBatchResponseSchema> extends keyof M.AiPipelineBatchResponse ? true : never) & (keyof M.AiPipelineBatchResponse extends keyof z.infer<typeof AiPipelineBatchResponseSchema> ? true : never) = true;
void _drift_AiPipelineBatchResponse;
const _drift_EntityPairField: (M.EntityPairField extends z.infer<typeof EntityPairFieldSchema> ? true : never) & (keyof z.infer<typeof EntityPairFieldSchema> extends keyof M.EntityPairField ? true : never) & (keyof M.EntityPairField extends keyof z.infer<typeof EntityPairFieldSchema> ? true : never) = true;
void _drift_EntityPairField;
const _drift_EntityPairSide: (M.EntityPairSide extends z.infer<typeof EntityPairSideSchema> ? true : never) & (keyof z.infer<typeof EntityPairSideSchema> extends keyof M.EntityPairSide ? true : never) & (keyof M.EntityPairSide extends keyof z.infer<typeof EntityPairSideSchema> ? true : never) = true;
void _drift_EntityPairSide;
const _drift_EntityPairPrior: (M.EntityPairPrior extends z.infer<typeof EntityPairPriorSchema> ? true : never) & (keyof z.infer<typeof EntityPairPriorSchema> extends keyof M.EntityPairPrior ? true : never) & (keyof M.EntityPairPrior extends keyof z.infer<typeof EntityPairPriorSchema> ? true : never) = true;
void _drift_EntityPairPrior;
const _drift_EntityPairCandidate: (M.EntityPairCandidate extends z.infer<typeof EntityPairCandidateSchema> ? true : never) & (keyof z.infer<typeof EntityPairCandidateSchema> extends keyof M.EntityPairCandidate ? true : never) & (keyof M.EntityPairCandidate extends keyof z.infer<typeof EntityPairCandidateSchema> ? true : never) = true;
void _drift_EntityPairCandidate;
const _drift_EntityPairInput: (M.EntityPairInput extends z.infer<typeof EntityPairInputSchema> ? true : never) & (keyof z.infer<typeof EntityPairInputSchema> extends keyof M.EntityPairInput ? true : never) & (keyof M.EntityPairInput extends keyof z.infer<typeof EntityPairInputSchema> ? true : never) = true;
void _drift_EntityPairInput;
const _drift_EntityPairMatch: (M.EntityPairMatch extends z.infer<typeof EntityPairMatchSchema> ? true : never) & (keyof z.infer<typeof EntityPairMatchSchema> extends keyof M.EntityPairMatch ? true : never) & (keyof M.EntityPairMatch extends keyof z.infer<typeof EntityPairMatchSchema> ? true : never) = true;
void _drift_EntityPairMatch;
const _drift_EntityPairMatchResult: (M.EntityPairMatchResult extends z.infer<typeof EntityPairMatchResultSchema> ? true : never) & (keyof z.infer<typeof EntityPairMatchResultSchema> extends keyof M.EntityPairMatchResult ? true : never) & (keyof M.EntityPairMatchResult extends keyof z.infer<typeof EntityPairMatchResultSchema> ? true : never) = true;
void _drift_EntityPairMatchResult;
const _drift_AiMatchV2Response: (M.AiMatchV2Response extends z.infer<typeof AiMatchV2ResponseSchema> ? true : never) & (keyof z.infer<typeof AiMatchV2ResponseSchema> extends keyof M.AiMatchV2Response ? true : never) & (keyof M.AiMatchV2Response extends keyof z.infer<typeof AiMatchV2ResponseSchema> ? true : never) = true;
void _drift_AiMatchV2Response;
const _drift_AiPipelineResponse: (M.AiPipelineResponse extends z.infer<typeof AiPipelineResponseSchema> ? true : never) & (keyof z.infer<typeof AiPipelineResponseSchema> extends keyof M.AiPipelineResponse ? true : never) & (keyof M.AiPipelineResponse extends keyof z.infer<typeof AiPipelineResponseSchema> ? true : never) = true;
void _drift_AiPipelineResponse;
const _drift_AiMatchResponse: (M.AiMatchResponse extends z.infer<typeof AiMatchResponseSchema> ? true : never) & (keyof z.infer<typeof AiMatchResponseSchema> extends keyof M.AiMatchResponse ? true : never) & (keyof M.AiMatchResponse extends keyof z.infer<typeof AiMatchResponseSchema> ? true : never) = true;
void _drift_AiMatchResponse;
const _drift_ChangeClassification: (M.ChangeClassification extends z.infer<typeof ChangeClassificationSchema> ? true : never) & (keyof z.infer<typeof ChangeClassificationSchema> extends keyof M.ChangeClassification ? true : never) & (keyof M.ChangeClassification extends keyof z.infer<typeof ChangeClassificationSchema> ? true : never) = true;
void _drift_ChangeClassification;
const _drift_IntelligenceClassification: (M.IntelligenceClassification extends z.infer<typeof IntelligenceClassificationSchema> ? true : never) & (keyof z.infer<typeof IntelligenceClassificationSchema> extends keyof M.IntelligenceClassification ? true : never) & (keyof M.IntelligenceClassification extends keyof z.infer<typeof IntelligenceClassificationSchema> ? true : never) = true;
void _drift_IntelligenceClassification;
const _drift_SuggestionProposal: (M.SuggestionProposal extends z.infer<typeof SuggestionProposalSchema> ? true : never) & (keyof z.infer<typeof SuggestionProposalSchema> extends keyof M.SuggestionProposal ? true : never) & (keyof M.SuggestionProposal extends keyof z.infer<typeof SuggestionProposalSchema> ? true : never) = true;
void _drift_SuggestionProposal;
const _drift_VersionSimulation: (M.VersionSimulation extends z.infer<typeof VersionSimulationSchema> ? true : never) & (keyof z.infer<typeof VersionSimulationSchema> extends keyof M.VersionSimulation ? true : never) & (keyof M.VersionSimulation extends keyof z.infer<typeof VersionSimulationSchema> ? true : never) = true;
void _drift_VersionSimulation;
const _drift_MigrationOp: (M.MigrationOp extends z.infer<typeof MigrationOpSchema> ? true : never) & (keyof z.infer<typeof MigrationOpSchema> extends keyof M.MigrationOp ? true : never) & (keyof M.MigrationOp extends keyof z.infer<typeof MigrationOpSchema> ? true : never) = true;
void _drift_MigrationOp;
const _drift_MigrationPlan: (M.MigrationPlan extends z.infer<typeof MigrationPlanSchema> ? true : never) & (keyof z.infer<typeof MigrationPlanSchema> extends keyof M.MigrationPlan ? true : never) & (keyof M.MigrationPlan extends keyof z.infer<typeof MigrationPlanSchema> ? true : never) = true;
void _drift_MigrationPlan;
